/**
 * WhispererConfig Component
 *
 * Admin component for Whisperer configuration:
 * - Rule management (CRUD) with scope selector
 * - Trigger configuration (frequency, on_error, on_tool, on_event)
 * - Template editing with variants
 *
 * @module components/admin/WhispererConfig
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  MessageSquare,
  RefreshCw,
  Loader2,
  AlertCircle,
  Plus,
  Trash2,
  Edit,
  Save,
  X,
  Check,
  Settings,
  Zap,
  Globe,
  Folder,
  Bot,
  Clock,
  AlertTriangle,
  Wrench,
  Calendar,
  ChevronDown,
  ChevronUp,
  Copy,
  ToggleLeft,
  ToggleRight
} from 'lucide-react';

// ============================================
// Constants
// ============================================

const API_BASE = '/api';

const TRIGGER_TYPES = [
  { value: 'frequency', label: 'Frequency', icon: Clock, description: 'Every N tool uses' },
  { value: 'on_error', label: 'On Error', icon: AlertTriangle, description: 'After tool errors' },
  { value: 'on_tool', label: 'On Tool', icon: Wrench, description: 'After specific tools' },
  { value: 'on_event', label: 'On Event', icon: Calendar, description: 'On system events' },
];

const SCOPE_TYPES = [
  { value: 'global', label: 'Global', icon: Globe, description: 'All agents' },
  { value: 'project', label: 'Project', icon: Folder, description: 'Project agents' },
  { value: 'agent', label: 'Agent', icon: Bot, description: 'Single agent' },
];

const AVAILABLE_TOOLS = ['Edit', 'Write', 'Read', 'Bash', 'Glob', 'Grep', 'WebFetch'];
const AVAILABLE_EVENTS = ['session_start', 'session_resume', 'task_assigned'];

// ============================================
// API Functions
// ============================================

async function fetchWhisperRules(scopeFilter = {}) {
  const params = new URLSearchParams();
  if (scopeFilter.scopeType) params.append('scopeType', scopeFilter.scopeType);
  if (scopeFilter.projectId) params.append('projectId', scopeFilter.projectId);
  if (scopeFilter.agentId) params.append('agentId', scopeFilter.agentId);

  const url = `${API_BASE}/whisper-rules${params.toString() ? '?' + params.toString() : ''}`;
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch rules');
  const result = await res.json();
  return Array.isArray(result) ? result : (result.data || []);
}

async function createWhisperRule(ruleData) {
  const res = await fetch(`${API_BASE}/whisper-rules`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(ruleData)
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.message || 'Failed to create rule');
  }
  const result = await res.json();
  return result.data || result;
}

async function updateWhisperRule(id, ruleData) {
  const res = await fetch(`${API_BASE}/whisper-rules/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(ruleData)
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.message || 'Failed to update rule');
  }
  const result = await res.json();
  return result.data || result;
}

async function deleteWhisperRule(id) {
  const res = await fetch(`${API_BASE}/whisper-rules/${id}`, {
    method: 'DELETE',
    credentials: 'include'
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.message || 'Failed to delete rule');
  }
}

async function fetchProjects() {
  const res = await fetch(`${API_BASE}/projects`, { credentials: 'include' });
  if (!res.ok) return [];
  const result = await res.json();
  return Array.isArray(result) ? result : (result.data || []);
}

async function fetchAgents() {
  const res = await fetch(`${API_BASE}/agents`, { credentials: 'include' });
  if (!res.ok) return [];
  const result = await res.json();
  return Array.isArray(result) ? result : (result.data || []);
}

// ============================================
// Sub-Components
// ============================================

/**
 * Toggle Switch Component
 */
function Toggle({ enabled, onChange, disabled = false }) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!enabled)}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        enabled ? 'bg-indigo-600' : 'bg-gray-600'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          enabled ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

/**
 * Scope Badge
 */
function ScopeBadge({ scopeType }) {
  const scope = SCOPE_TYPES.find(s => s.value === scopeType) || SCOPE_TYPES[0];
  const Icon = scope.icon;

  const colors = {
    global: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    project: 'bg-green-500/20 text-green-400 border-green-500/30',
    agent: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  };

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs border ${colors[scopeType] || colors.global}`}>
      <Icon className="w-3 h-3" />
      {scope.label}
    </span>
  );
}

/**
 * Trigger Badge
 */
function TriggerBadge({ triggerType, triggerConfig }) {
  const trigger = TRIGGER_TYPES.find(t => t.value === triggerType) || TRIGGER_TYPES[0];
  const Icon = trigger.icon;

  let detail = '';
  if (triggerType === 'frequency' && triggerConfig?.every) {
    detail = `every ${triggerConfig.every}`;
  } else if (triggerType === 'on_tool' && triggerConfig?.tools) {
    detail = triggerConfig.tools.join(', ');
  } else if (triggerType === 'on_event' && triggerConfig?.event) {
    detail = triggerConfig.event;
  }

  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-gray-700 text-gray-300">
      <Icon className="w-3 h-3" />
      {trigger.label}
      {detail && <span className="text-gray-500">({detail})</span>}
    </span>
  );
}

/**
 * Rule Form Component
 */
function RuleForm({ rule, onSubmit, onCancel, isSubmitting, projects = [], agents = [] }) {
  const isEditing = !!rule?.id;

  const [formData, setFormData] = useState({
    name: rule?.name || '',
    description: rule?.description || '',
    enabled: rule?.enabled ?? true,
    scopeType: rule?.scopeType || 'global',
    projectId: rule?.projectId || '',
    agentId: rule?.agentId || '',
    triggerType: rule?.triggerType || 'frequency',
    triggerConfig: rule?.triggerConfig || { every: 10 },
    template: rule?.template || '',
    templateVariants: rule?.templateVariants || [],
    suppressAfterInsight: rule?.suppressAfterInsight ?? true,
    suppressWindow: rule?.suppressWindow || 5,
    priority: rule?.priority || 10,
  });

  const [newVariant, setNewVariant] = useState('');

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleTriggerConfigChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      triggerConfig: { ...prev.triggerConfig, [key]: value }
    }));
  };

  const addVariant = () => {
    if (newVariant.trim()) {
      setFormData(prev => ({
        ...prev,
        templateVariants: [...prev.templateVariants, newVariant.trim()]
      }));
      setNewVariant('');
    }
  };

  const removeVariant = (index) => {
    setFormData(prev => ({
      ...prev,
      templateVariants: prev.templateVariants.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Clean up scope-specific fields
    const submitData = { ...formData };
    if (submitData.scopeType === 'global') {
      delete submitData.projectId;
      delete submitData.agentId;
    } else if (submitData.scopeType === 'project') {
      delete submitData.agentId;
    } else if (submitData.scopeType === 'agent') {
      delete submitData.projectId;
    }

    onSubmit(submitData);
  };

  return (
    <form onSubmit={handleSubmit} className="bg-gray-800/50 rounded-lg border border-gray-700 p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-white">
          {isEditing ? 'Edit Rule' : 'New Rule'}
        </h3>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="p-1.5 rounded text-gray-400 hover:text-white hover:bg-gray-700"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Name & Description */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs text-gray-400 mb-1">Name</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500"
            placeholder="Rule name"
            required
          />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Priority</label>
          <input
            type="number"
            value={formData.priority}
            onChange={(e) => handleChange('priority', parseInt(e.target.value, 10))}
            className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500"
            min="1"
            max="100"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs text-gray-400 mb-1">Description</label>
        <input
          type="text"
          value={formData.description}
          onChange={(e) => handleChange('description', e.target.value)}
          className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500"
          placeholder="Optional description"
        />
      </div>

      {/* Scope */}
      <div>
        <label className="block text-xs text-gray-400 mb-2">Scope</label>
        <div className="flex gap-2">
          {SCOPE_TYPES.map((scope) => {
            const Icon = scope.icon;
            const isSelected = formData.scopeType === scope.value;
            return (
              <button
                key={scope.value}
                type="button"
                onClick={() => handleChange('scopeType', scope.value)}
                className={`flex items-center gap-2 px-3 py-2 rounded border text-sm transition-colors ${
                  isSelected
                    ? 'border-indigo-500 bg-indigo-500/20 text-indigo-400'
                    : 'border-gray-700 bg-gray-800 text-gray-400 hover:border-gray-600'
                }`}
              >
                <Icon className="w-4 h-4" />
                {scope.label}
              </button>
            );
          })}
        </div>

        {/* Project/Agent Selector */}
        {formData.scopeType === 'project' && (
          <div className="mt-2">
            <select
              value={formData.projectId}
              onChange={(e) => handleChange('projectId', e.target.value)}
              className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded text-sm text-white focus:outline-none focus:border-indigo-500"
              required
            >
              <option value="">Select project...</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
        )}

        {formData.scopeType === 'agent' && (
          <div className="mt-2">
            <select
              value={formData.agentId}
              onChange={(e) => handleChange('agentId', e.target.value)}
              className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded text-sm text-white focus:outline-none focus:border-indigo-500"
              required
            >
              <option value="">Select agent...</option>
              {agents.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Trigger */}
      <div>
        <label className="block text-xs text-gray-400 mb-2">Trigger</label>
        <div className="flex gap-2 flex-wrap">
          {TRIGGER_TYPES.map((trigger) => {
            const Icon = trigger.icon;
            const isSelected = formData.triggerType === trigger.value;
            return (
              <button
                key={trigger.value}
                type="button"
                onClick={() => {
                  handleChange('triggerType', trigger.value);
                  // Reset trigger config for new type
                  if (trigger.value === 'frequency') {
                    handleChange('triggerConfig', { every: 10 });
                  } else if (trigger.value === 'on_tool') {
                    handleChange('triggerConfig', { tools: [] });
                  } else if (trigger.value === 'on_event') {
                    handleChange('triggerConfig', { event: 'session_start' });
                  } else {
                    handleChange('triggerConfig', {});
                  }
                }}
                className={`flex items-center gap-2 px-3 py-2 rounded border text-sm transition-colors ${
                  isSelected
                    ? 'border-indigo-500 bg-indigo-500/20 text-indigo-400'
                    : 'border-gray-700 bg-gray-800 text-gray-400 hover:border-gray-600'
                }`}
              >
                <Icon className="w-4 h-4" />
                {trigger.label}
              </button>
            );
          })}
        </div>

        {/* Trigger Config */}
        <div className="mt-2">
          {formData.triggerType === 'frequency' && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-400">Every</span>
              <input
                type="number"
                value={formData.triggerConfig.every || 10}
                onChange={(e) => handleTriggerConfigChange('every', parseInt(e.target.value, 10))}
                className="w-20 px-3 py-1.5 bg-gray-900 border border-gray-700 rounded text-sm text-white focus:outline-none focus:border-indigo-500"
                min="1"
              />
              <span className="text-sm text-gray-400">tool uses</span>
            </div>
          )}

          {formData.triggerType === 'on_tool' && (
            <div className="flex flex-wrap gap-2">
              {AVAILABLE_TOOLS.map((tool) => {
                const isSelected = formData.triggerConfig.tools?.includes(tool);
                return (
                  <button
                    key={tool}
                    type="button"
                    onClick={() => {
                      const tools = formData.triggerConfig.tools || [];
                      if (isSelected) {
                        handleTriggerConfigChange('tools', tools.filter(t => t !== tool));
                      } else {
                        handleTriggerConfigChange('tools', [...tools, tool]);
                      }
                    }}
                    className={`px-2 py-1 rounded text-xs transition-colors ${
                      isSelected
                        ? 'bg-indigo-500/20 text-indigo-400 border border-indigo-500/50'
                        : 'bg-gray-800 text-gray-400 border border-gray-700 hover:border-gray-600'
                    }`}
                  >
                    {tool}
                  </button>
                );
              })}
            </div>
          )}

          {formData.triggerType === 'on_event' && (
            <select
              value={formData.triggerConfig.event || 'session_start'}
              onChange={(e) => handleTriggerConfigChange('event', e.target.value)}
              className="px-3 py-1.5 bg-gray-900 border border-gray-700 rounded text-sm text-white focus:outline-none focus:border-indigo-500"
            >
              {AVAILABLE_EVENTS.map((event) => (
                <option key={event} value={event}>{event}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Template */}
      <div>
        <label className="block text-xs text-gray-400 mb-1">Template</label>
        <textarea
          value={formData.template}
          onChange={(e) => handleChange('template', e.target.value)}
          className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500 font-mono"
          rows={3}
          placeholder="Reminder message template..."
          required
        />
      </div>

      {/* Template Variants */}
      <div>
        <label className="block text-xs text-gray-400 mb-1">Template Variants (for rotation)</label>
        <div className="space-y-2">
          {formData.templateVariants.map((variant, index) => (
            <div key={index} className="flex items-start gap-2">
              <span className="text-xs text-gray-500 mt-2">{index + 1}.</span>
              <div className="flex-1 px-3 py-2 bg-gray-900 border border-gray-700 rounded text-sm text-gray-300 font-mono">
                {variant}
              </div>
              <button
                type="button"
                onClick={() => removeVariant(index)}
                className="p-1.5 rounded text-gray-400 hover:text-red-400 hover:bg-gray-700"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
          <div className="flex items-start gap-2">
            <textarea
              value={newVariant}
              onChange={(e) => setNewVariant(e.target.value)}
              className="flex-1 px-3 py-2 bg-gray-900 border border-gray-700 rounded text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500 font-mono"
              rows={2}
              placeholder="Add variant..."
            />
            <button
              type="button"
              onClick={addVariant}
              disabled={!newVariant.trim()}
              className="p-2 rounded bg-gray-700 text-gray-300 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Suppress Settings */}
      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2 text-sm text-gray-300">
          <Toggle
            enabled={formData.suppressAfterInsight}
            onChange={(v) => handleChange('suppressAfterInsight', v)}
          />
          Suppress after insight
        </label>
        {formData.suppressAfterInsight && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-400">for</span>
            <input
              type="number"
              value={formData.suppressWindow}
              onChange={(e) => handleChange('suppressWindow', parseInt(e.target.value, 10))}
              className="w-16 px-2 py-1 bg-gray-900 border border-gray-700 rounded text-sm text-white focus:outline-none focus:border-indigo-500"
              min="1"
            />
            <span className="text-sm text-gray-400">tool uses</span>
          </div>
        )}
      </div>

      {/* Enabled Toggle */}
      <div className="flex items-center gap-2">
        <Toggle
          enabled={formData.enabled}
          onChange={(v) => handleChange('enabled', v)}
        />
        <span className="text-sm text-gray-300">Rule enabled</span>
      </div>

      {/* Submit */}
      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded text-sm text-gray-400 hover:text-white hover:bg-gray-700"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting || !formData.name || !formData.template}
          className="flex items-center gap-2 px-4 py-2 rounded text-sm bg-indigo-600 text-white hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              {isEditing ? 'Update' : 'Create'}
            </>
          )}
        </button>
      </div>
    </form>
  );
}

/**
 * Rules Section
 */
function RulesSection({
  rules,
  onRuleCreate,
  onRuleUpdate,
  onRuleDelete,
  isLoading,
  projects,
  agents,
  scopeFilter,
  onScopeFilterChange
}) {
  const [showForm, setShowForm] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [expandedRuleId, setExpandedRuleId] = useState(null);

  const handleSubmit = async (formData) => {
    setIsSubmitting(true);
    try {
      if (editingRule) {
        await onRuleUpdate(editingRule.id, formData);
      } else {
        await onRuleCreate(formData);
      }
      setShowForm(false);
      setEditingRule(null);
    } catch (err) {
      console.error('Failed to save rule:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (rule) => {
    setEditingRule(rule);
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingRule(null);
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await onRuleDelete(id);
    } catch (err) {
      console.error('Failed to delete rule:', err);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-amber-500/10">
            <Zap className="w-5 h-5 text-amber-400" />
          </div>
          <h2 className="text-lg font-semibold text-white">Whisper Rules</h2>
        </div>
        <div className="flex items-center gap-2">
          {/* Scope Filter */}
          <select
            value={scopeFilter.scopeType || ''}
            onChange={(e) => onScopeFilterChange({ ...scopeFilter, scopeType: e.target.value || undefined })}
            className="px-3 py-1.5 bg-gray-900 border border-gray-700 rounded text-sm text-white focus:outline-none focus:border-indigo-500"
          >
            <option value="">All Scopes</option>
            {SCOPE_TYPES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>

          {!showForm && (
            <button
              type="button"
              onClick={() => setShowForm(true)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-md text-sm bg-gray-700 hover:bg-gray-600 text-gray-300"
            >
              <Plus className="w-4 h-4" />
              Add Rule
            </button>
          )}
        </div>
      </div>

      {showForm && (
        <div className="mb-4">
          <RuleForm
            rule={editingRule}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isSubmitting={isSubmitting}
            projects={projects}
            agents={agents}
          />
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
        </div>
      ) : rules.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No rules found. Create one to get started.
        </div>
      ) : (
        <div className="space-y-2">
          {rules.map((rule) => (
            <div
              key={rule.id}
              className="bg-gray-800/50 rounded-lg border border-gray-700/50 hover:border-gray-600 transition-colors"
            >
              {/* Rule Header */}
              <div className="flex items-center justify-between p-3">
                <div className="flex items-center gap-3">
                  <Toggle
                    enabled={rule.enabled}
                    onChange={(v) => onRuleUpdate(rule.id, { enabled: v })}
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-medium ${rule.enabled ? 'text-white' : 'text-gray-500'}`}>
                        {rule.name}
                      </span>
                      <ScopeBadge scopeType={rule.scopeType} />
                      <TriggerBadge triggerType={rule.triggerType} triggerConfig={rule.triggerConfig} />
                    </div>
                    {rule.description && (
                      <p className="text-xs text-gray-500 mt-0.5">{rule.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Priority: {rule.priority}</span>
                  <button
                    type="button"
                    onClick={() => setExpandedRuleId(expandedRuleId === rule.id ? null : rule.id)}
                    className="p-1.5 rounded text-gray-400 hover:text-white hover:bg-gray-700"
                    title="Toggle details"
                  >
                    {expandedRuleId === rule.id ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleEdit(rule)}
                    className="p-1.5 rounded text-gray-400 hover:text-white hover:bg-gray-700"
                    title="Edit"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(rule.id)}
                    disabled={deletingId === rule.id}
                    className="p-1.5 rounded text-gray-400 hover:text-red-400 hover:bg-gray-700 disabled:opacity-50"
                    title="Delete"
                  >
                    {deletingId === rule.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              {/* Expanded Details */}
              {expandedRuleId === rule.id && (
                <div className="border-t border-gray-700/50 p-3 space-y-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Template</label>
                    <div className="px-3 py-2 bg-gray-900/50 rounded text-sm text-gray-300 font-mono whitespace-pre-wrap">
                      {rule.template}
                    </div>
                  </div>
                  {rule.templateVariants && rule.templateVariants.length > 0 && (
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Variants ({rule.templateVariants.length})</label>
                      <div className="space-y-1">
                        {rule.templateVariants.map((variant, i) => (
                          <div key={i} className="px-3 py-1.5 bg-gray-900/50 rounded text-xs text-gray-400 font-mono">
                            {i + 1}. {variant}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="flex gap-4 text-xs text-gray-500">
                    <span>Suppress after insight: {rule.suppressAfterInsight ? `Yes (${rule.suppressWindow} tools)` : 'No'}</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================
// Main Component
// ============================================

export default function WhispererConfig() {
  // State
  const [rules, setRules] = useState([]);
  const [projects, setProjects] = useState([]);
  const [agents, setAgents] = useState([]);
  const [isLoadingRules, setIsLoadingRules] = useState(true);
  const [error, setError] = useState(null);
  const [scopeFilter, setScopeFilter] = useState({});

  // Load data
  const loadData = useCallback(async () => {
    setError(null);

    // Load projects and agents for scope selectors
    try {
      const [projectsData, agentsData] = await Promise.all([
        fetchProjects(),
        fetchAgents()
      ]);
      setProjects(projectsData);
      setAgents(agentsData);
    } catch (err) {
      console.error('Failed to load projects/agents:', err);
    }

    // Load rules
    try {
      setIsLoadingRules(true);
      const rulesData = await fetchWhisperRules(scopeFilter);
      setRules(rulesData);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoadingRules(false);
    }
  }, [scopeFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handlers
  const handleRuleCreate = useCallback(async (ruleData) => {
    const newRule = await createWhisperRule(ruleData);
    setRules(prev => [...prev, newRule]);
  }, []);

  const handleRuleUpdate = useCallback(async (id, ruleData) => {
    const updatedRule = await updateWhisperRule(id, ruleData);
    setRules(prev => prev.map(r => r.id === id ? { ...r, ...updatedRule } : r));
  }, []);

  const handleRuleDelete = useCallback(async (id) => {
    await deleteWhisperRule(id);
    setRules(prev => prev.filter(r => r.id !== id));
  }, []);

  // Render
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-amber-500/10">
            <MessageSquare className="w-6 h-6 text-amber-400" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-white">Whisperer</h1>
            <p className="text-sm text-gray-400">Configure reminder rules for agents</p>
          </div>
        </div>
        <button
          onClick={loadData}
          disabled={isLoadingRules}
          className="flex items-center gap-2 px-3 py-1.5 rounded-md text-sm bg-gray-700 hover:bg-gray-600 text-gray-300 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${isLoadingRules ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Rules Section */}
      <RulesSection
        rules={rules}
        onRuleCreate={handleRuleCreate}
        onRuleUpdate={handleRuleUpdate}
        onRuleDelete={handleRuleDelete}
        isLoading={isLoadingRules}
        projects={projects}
        agents={agents}
        scopeFilter={scopeFilter}
        onScopeFilterChange={setScopeFilter}
      />
    </div>
  );
}

// Named Exports
export {
  RulesSection,
  RuleForm,
  Toggle,
  ScopeBadge,
  TriggerBadge
};
